/**
 * Worker auth context. Same shape as admin's `useAuth`, but worker flow is simpler:
 * email + password → DONE. No MFA, no TOTP. Workers may still receive a temporary
 * password (admin-issued via Cognito), so we still handle NEW_PASSWORD_REQUIRED.
 */
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  fetchAuthSession,
  getCurrentUser,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
  type SignInOutput,
} from 'aws-amplify/auth';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { jwtClaimsSchema } from '@fin/shared/schemas';
import type { CognitoGroup } from '@fin/shared/enums';

export interface AuthUser {
  userId: string;
  email: string;
  groups: CognitoGroup[];
}

export type AuthStatus = 'loading' | 'unauthenticated' | 'authenticated';

export interface SignInChallenge {
  challenge: SignInOutput['nextStep']['signInStep'];
}

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  signIn: (email: string, password: string) => Promise<SignInChallenge | { user: AuthUser }>;
  respondToChallenge: (response: string) => Promise<SignInChallenge | { user: AuthUser }>;
  requestPasswordReset: (email: string) => Promise<void>;
  confirmPasswordReset: (email: string, code: string, newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function loadCurrentUser(): Promise<AuthUser | null> {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    if (!idToken) return null;
    const claims = jwtClaimsSchema.parse(idToken.payload);
    const current = await getCurrentUser();
    return {
      userId: claims.sub,
      email: claims.email ?? current.signInDetails?.loginId ?? '',
      groups: [...claims['cognito:groups']] as CognitoGroup[],
    };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    void loadCurrentUser().then((u) => {
      setUser(u);
      setStatus(u ? 'authenticated' : 'unauthenticated');
    });
  }, []);

  const refresh = useCallback(async () => {
    const u = await loadCurrentUser();
    setUser(u);
    setStatus(u ? 'authenticated' : 'unauthenticated');
    return u;
  }, []);

  const handleStep = useCallback(
    async (output: SignInOutput): Promise<SignInChallenge | { user: AuthUser }> => {
      const step = output.nextStep.signInStep;
      if (step === 'DONE') {
        const u = await refresh();
        if (!u) throw new Error('Sign-in completed but no session was created.');
        return { user: u };
      }
      return { challenge: step };
    },
    [refresh],
  );

  const signIn = useCallback<AuthContextValue['signIn']>(
    async (email, password) => {
      // Amplify v6 throws `UserAlreadyAuthenticatedException` if a session is
      // already cached in localStorage. Clear it first so re-login always works.
      try {
        await amplifySignOut();
      } catch {
        /* nothing to sign out */
      }
      const output = await amplifySignIn({
        username: email.trim().toLowerCase(),
        password,
        options: { authFlowType: 'USER_PASSWORD_AUTH' },
      });
      return handleStep(output);
    },
    [handleStep],
  );

  const respondToChallenge = useCallback<AuthContextValue['respondToChallenge']>(
    async (response) => {
      const output = await confirmSignIn({ challengeResponse: response });
      return handleStep(output);
    },
    [handleStep],
  );

  const requestPasswordReset = useCallback<AuthContextValue['requestPasswordReset']>(async (email) => {
    await resetPassword({ username: email.trim().toLowerCase() });
  }, []);

  const confirmPasswordReset = useCallback<AuthContextValue['confirmPasswordReset']>(
    async (email, code, newPassword) => {
      await confirmResetPassword({
        username: email.trim().toLowerCase(),
        confirmationCode: code,
        newPassword,
      });
    },
    [],
  );

  const signOut = useCallback<AuthContextValue['signOut']>(async () => {
    await amplifySignOut();
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  const getIdToken = useCallback<AuthContextValue['getIdToken']>(async () => {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, signIn, respondToChallenge, requestPasswordReset, confirmPasswordReset, signOut, getIdToken }),
    [status, user, signIn, respondToChallenge, requestPasswordReset, confirmPasswordReset, signOut, getIdToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>.');
  return ctx;
}
